import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { FormField, Input, Select, Textarea } from '../ui/Form';
import { ArrowDownToLine, Trash2, AlertTriangle } from 'lucide-react';
import { useTreasury } from '../../hooks/useTreasury';
import type {
  FixedAsset,
  DisposeFixedAssetPayload,
  ScrapFixedAssetPayload,
} from '../../types/fixed-assets';

interface DisposeFixedAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  asset: FixedAsset | null;
  onDispose: (payload: DisposeFixedAssetPayload) => Promise<void>;
  onScrap: (payload: ScrapFixedAssetPayload) => Promise<void>;
}

type Mode = 'sale' | 'scrap';

/**
 * Cession (vente) ou mise au rebut d'une immobilisation.
 *
 * Couvre les deux cas dans une seule modale via un toggle, car ils partagent
 * le même contexte (asset, comptes SYSCOHADA 81/82) et seul le scénario varie :
 *  - VENTE : encaissement + plus/moins-value (différence 82 − 81)
 *  - REBUT : perte sèche, pas d'encaissement, VNC en charge exceptionnelle (81)
 *
 * Les codes de comptes 81/82 sont préremplis avec les valeurs SYSCOHADA standard
 * mais restent éditables (cas où l'entreprise utilise un découpage 812/822).
 */
export function DisposeFixedAssetModal({
  isOpen,
  onClose,
  asset,
  onDispose,
  onScrap,
}: DisposeFixedAssetModalProps) {
  const [mode, setMode] = useState<Mode>('sale');
  const [loading, setLoading] = useState(false);

  const { accounts: treasuryAccounts, loadAccounts } = useTreasury();
  useEffect(() => {
    if (isOpen) {
      void loadAccounts();
    }
  }, [isOpen, loadAccounts]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [date, setDate] = useState(today);
  const [proceeds, setProceeds] = useState('');
  const [proceedsAccountId, setProceedsAccountId] = useState('');
  const [vncAccountCode, setVncAccountCode] = useState('81');
  const [proceedsRevenueAccountCode, setProceedsRevenueAccountCode] = useState('82');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  // Reset state when reopening on a new asset
  useEffect(() => {
    if (!isOpen) return;
    setMode('sale');
    setDate(today);
    setProceeds('');
    setProceedsAccountId('');
    setVncAccountCode('81');
    setProceedsRevenueAccountCode('82');
    setReference('');
    setNotes('');
  }, [isOpen, asset?.id, today]);

  const netBookValue = asset
    ? Number(asset.acquisitionCost) - Number(asset.cumulativeDepreciation)
    : 0;
  const proceedsNumber = Number(proceeds) || 0;
  const gainLoss = proceedsNumber - netBookValue;

  const treasuryOptions = treasuryAccounts.map((acc: any) => ({
    value: acc.id,
    label: `${acc.name ?? acc.bankName ?? 'Compte'} (${acc.accountNumber ?? '—'}) — ${acc.currency}`,
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!asset) return;
    setLoading(true);
    try {
      if (mode === 'sale') {
        await onDispose({
          disposalDate: date,
          disposalProceeds: proceedsNumber,
          proceedsAccountId,
          vncAccountCode: vncAccountCode.trim(),
          proceedsRevenueAccountCode: proceedsRevenueAccountCode.trim(),
          reference: reference.trim() || undefined,
          notes: notes.trim() || undefined,
        });
      } else {
        await onScrap({
          scrapDate: date,
          vncAccountCode: vncAccountCode.trim(),
          reason: notes.trim() || undefined,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!asset) return null;

  const title = mode === 'sale' ? `Cession — ${asset.name}` : `Mise au rebut — ${asset.name}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Toggle mode */}
        <div className="inline-flex rounded-lg border border-border p-1 bg-muted/30">
          <button
            type="button"
            onClick={() => setMode('sale')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
              mode === 'sale'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <ArrowDownToLine className="h-4 w-4" />
            Cession (vente)
          </button>
          <button
            type="button"
            onClick={() => setMode('scrap')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
              mode === 'scrap'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Trash2 className="h-4 w-4" />
            Mise au rebut
          </button>
        </div>

        {/* Récap immobilisation */}
        <div className="grid grid-cols-3 gap-4 p-4 rounded-lg bg-muted/30">
          <div>
            <p className="text-xs text-muted-foreground">Coût d'acquisition</p>
            <p className="font-semibold">{Number(asset.acquisitionCost).toLocaleString('fr-FR')}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Amort. cumulés</p>
            <p className="font-semibold">{Number(asset.cumulativeDepreciation).toLocaleString('fr-FR')}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">VNC</p>
            <p className="font-semibold text-primary">
              {netBookValue.toLocaleString('fr-FR')}
            </p>
          </div>
        </div>

        {/* Avertissement */}
        <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <p>
            {mode === 'sale'
              ? "Cette action génère une écriture de cession et marque l'immobilisation comme cédée. Action irréversible (extourne possible via le journal)."
              : "La VNC restante part en charge exceptionnelle. L'immobilisation est marquée cédée."}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField label={mode === 'sale' ? 'Date de cession' : 'Date de mise au rebut'} required>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </FormField>
          <FormField label="Compte VNC (classe 81)" required hint="Code SYSCOHADA — par défaut 81">
            <Input
              value={vncAccountCode}
              onChange={(e) => setVncAccountCode(e.target.value)}
              required
            />
          </FormField>
        </div>

        {mode === 'sale' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Valeur de cession" required>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={proceeds}
                  onChange={(e) => setProceeds(e.target.value)}
                  required
                />
              </FormField>
              <FormField label="Compte produit (classe 82)" required hint="Code SYSCOHADA — par défaut 82">
                <Input
                  value={proceedsRevenueAccountCode}
                  onChange={(e) => setProceedsRevenueAccountCode(e.target.value)}
                  required
                />
              </FormField>
            </div>

            <FormField label="Compte d'encaissement (banque/caisse)" required>
              <Select
                value={proceedsAccountId}
                onChange={(e) => setProceedsAccountId(e.target.value)}
                options={[
                  { value: '', label: '— Sélectionner —' },
                  ...treasuryOptions,
                ]}
                required
              />
            </FormField>

            {proceedsNumber > 0 && (
              <div
                className={`text-sm font-medium p-3 rounded-lg ${
                  gainLoss >= 0
                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                    : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                }`}
              >
                {gainLoss >= 0 ? 'Plus-value' : 'Moins-value'} :{' '}
                {Math.abs(gainLoss).toLocaleString('fr-FR')}
              </div>
            )}

            <FormField label="Référence" hint="Numéro de contrat / facture de cession">
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </FormField>
          </>
        )}

        <FormField label={mode === 'sale' ? 'Notes' : 'Motif (vol, destruction, obsolescence…)'}>
          <Textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </FormField>

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant={mode === 'scrap' ? 'danger' : 'primary'}
            isLoading={loading}
            icon={mode === 'sale' ? ArrowDownToLine : Trash2}
          >
            {mode === 'sale' ? 'Comptabiliser la cession' : 'Comptabiliser le rebut'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
